/* eslint-disable @typescript-eslint/naming-convention */
import { uniqBy } from 'lodash';
import { supportDeleteEnv } from '../../../api/admin/game';
import {
  CommonButton,
  CommonButtonGroup,
} from '../../../components/button/main';
import { ModalWrap } from '../../../components/modal-wrap';
import { PaginationTable } from '../../../components/pagination-table';
import { CommonTagGroup } from '../../../components/tag';
import { getLocalTime, strToJson } from '../../../lib';
import { IPageGameManageItem } from '../../../model';
import {
  CommonStatusStep,
  IAPICompanyFullInfo,
  IAPIServicesOpenItem,
  IAPIUserInfo,
  RbacAdminPermObjectKey,
} from '../../../model/api-modules';
import { GameEnvRegion, IGameEnvModel } from '../../../model/api-modules/env';
import { renderEnvTaskStatus, renderEnvTypeTag } from '../../lib';
import { AdminPermCheck } from '../components/admin-permission-check';
import { getUserNameBySite } from '../lib/columns';
import { openEnvDetail } from './detail';
import styles from './index.module.scss';
import { isStudioCertified } from './lib';

export const getSubEnvTable = (
  companyList: IAPICompanyFullInfo[] = [],
  gameInfo: IPageGameManageItem,
  selectedKeys: number[] = [],
  disableSelect: boolean,
  isSelectAll: boolean,
  onChange: () => void,
  onSelectChange: (gameID: string, keys: number[]) => void,
  userInfo: IAPIUserInfo | null,
  site?: string
) => {
  const deleteHandler = (
    record: IGameEnvModel & {
      services: IAPIServicesOpenItem[];
    }
  ) => {
    ModalWrap.show({
      title: '删除环境',
      children: (
        <div>
          <div>是否确认删除以下环境？</div>
          <div>环境：{`${record.Name}(${record.id})`}</div>
          <div>所属游戏：{`${gameInfo.Name}(${gameInfo.GameID})`}</div>
          <div>
            所属工作室：
            {`${gameInfo.studioInfo?.Name}(${gameInfo.studioInfo?.StudioID})`}
          </div>
        </div>
      ),
      oKText: '删除',
      cancelText: '取消',
      danger: true,
      async onOk() {
        const res = await supportDeleteEnv(
          record.GameID,
          record.id,
          gameInfo.StudioID
        );
        onChange();
        return res;
      },
    });
  };
  return (
    <PaginationTable
      pagination={false}
      list={gameInfo.envs}
      tableExtension={{
        rowSelection: {
          selectedRowKeys: isSelectAll
            ? gameInfo.envs.map((env) => env.id)
            : selectedKeys,
          onChange(selectedRowKeys) {
            onSelectChange(gameInfo.GameID, selectedRowKeys as number[]);
          },
          getCheckboxProps(record) {
            if (!record) {
              const gameHasOpenedService =
                isStudioCertified(gameInfo.StudioID, companyList) ||
                gameInfo?.envs.some((env) =>
                  env.services.some(
                    (service) => service.Status === CommonStatusStep.Resolve
                  )
                );
              return {
                className: gameHasOpenedService ? styles.checkCannotSelect : '',
                disabled: gameHasOpenedService || disableSelect,
              };
            }
            const cannotSelect =
              isStudioCertified(gameInfo.StudioID, companyList) ||
              record?.services.some(
                (service) => service.Status === CommonStatusStep.Resolve
              );
            return {
              className: cannotSelect ? styles.checkCannotSelect : '',
              disabled: cannotSelect || disableSelect,
            };
          },
        },
      }}
      configFields={[
        {
          key: 'Type',
          name: '类型',
          tableExtension: {
            renderRow(_key, _val, record) {
              return renderEnvTypeTag(record.Type);
            },
          },
        },
        {
          key: 'id',
          name: 'ID',
        },
        {
          key: 'Name',
          name: '环境名称',
        },
        {
          key: 'Guid',
          name: 'EnvID',
        },
        {
          key: 'id',
          name: '状态',
          tableExtension: {
            renderRow(_key, _val, record) {
              return renderEnvTaskStatus(record.task);
            },
          },
        },
        {
          key: 'Region',
          name: '地区',
          tableExtension: {
            renderRow(_key, _val, record) {
              return Object.keys(GameEnvRegion).find(
                (name) => Object(GameEnvRegion)[name] === record.Region
              );
            },
          },
        },
        {
          key: 'id',
          name: '服务版本',
          tableExtension: {
            renderRow(_kay, _val, record) {
              const { GosParams } = record;
              return strToJson<Record<string, string>>(GosParams || '')
                ?.GameServiceVersion;
            },
          },
        },
        {
          key: 'id',
          name: '已开通服务',
          tableExtension: {
            renderRow(_kay, _val, record) {
              const { services } = record;
              const tipAcc = services.reduce<Record<string, string[]>>(
                (acc, service) => {
                  if (!service.ServiceName) {
                    return acc;
                  }
                  const currentSubService = acc[service.ServiceName] || [];
                  if (!service.SubServiceName) {
                    acc[service.ServiceName] = [...currentSubService];
                    return acc;
                  }

                  acc[service.ServiceName] = [
                    ...currentSubService,
                    String(service.SubServiceName),
                  ];
                  return acc;
                },
                {}
              );

              const tip = Object.keys(tipAcc)
                .map(
                  (name) =>
                    `${name}${
                      tipAcc[name]?.length > 0
                        ? `(${tipAcc[name].join('、')})`
                        : ''
                    }`
                )
                .join('、');

              return (
                <CommonTagGroup
                  tip={tip}
                  items={uniqBy(
                    services.filter(
                      (item) => item.Status === CommonStatusStep.Resolve
                    ),
                    'ServiceName'
                  ).map((service) => {
                    return {
                      text: service.ServiceName,
                      type: 'white',
                    };
                  })}
                />
              );
            },
          },
        },
        {
          key: 'Creator',
          name: '创建人',
          tableExtension: {
            renderRow(_kay, _val, record) {
              const { FounderID } = record;
              return getUserNameBySite(FounderID, site);
            },
          },
        },
        {
          key: 'CreateTime',
          name: '创建时间',
          tableExtension: {
            renderRow(_key, _val, record) {
              return getLocalTime(
                record.CreateTime,
                userInfo?.UserSettings?.Timezone
              );
            },
          },
        },
        {
          key: 'operate',
          name: '操作',
          tableExtension: {
            renderRow(_key, _val, record) {
              const hasOpenedService = !!record.services.length;
              const cannotDelete =
                hasOpenedService ||
                isStudioCertified(record.gameInfo.StudioID, companyList);
              const getTip = () => {
                if (isStudioCertified(record.gameInfo.StudioID, companyList)) {
                  return '已企业认证不支持删除';
                }
                if (hasOpenedService) {
                  return '已开通服务不支持删除';
                }
              };
              return (
                <CommonButtonGroup>
                  <CommonButton
                    type="link"
                    onClick={() => {
                      openEnvDetail(record, gameInfo, userInfo, site, onChange);
                    }}
                  >
                    详情
                  </CommonButton>
                  <AdminPermCheck
                    contentType="action"
                    permissionKey={RbacAdminPermObjectKey.GameManageEdit}
                    placement="bottom"
                    otherTips={getTip()}
                  >
                    <CommonButton
                      type="link"
                      danger
                      disabled={cannotDelete}
                      onClick={() => deleteHandler(record)}
                    >
                      删除
                    </CommonButton>
                  </AdminPermCheck>
                </CommonButtonGroup>
              );
            },
          },
        },
      ]}
    />
  );
};
